package br.com.htms.inspeccaocampo.ui.theme

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Shapes
import androidx.compose.material3.Typography
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import br.com.htms.inspeccaocampo.platform.gravarTemaEscuro
import br.com.htms.inspeccaocampo.platform.lerTemaEscuro

/** Tema claro ou escuro do portal. A escolha fica gravada neste aparelho. */
object TemaPortal {
    var escuro by mutableStateOf(lerTemaEscuro())
        private set

    fun sincronizar() {
        val salvo = lerTemaEscuro()
        if (salvo != escuro) escuro = salvo
    }

    fun alternar() {
        escuro = !escuro
        gravarTemaEscuro(escuro)
    }
}

/** Paleta do portal. No escuro, [Navy] clareia para titulo e [Marca] segue como fundo de botao. */
object PortalCores {
    private val escuro get() = TemaPortal.escuro

    val Navy: Color get() = if (escuro) Color(0xFFD5E4F7) else Color(0xFF0B2A55)
    val Marca: Color get() = if (escuro) Color(0xFF2E6BC4) else Color(0xFF0B2A55)
    val AzulIcone: Color get() = if (escuro) Color(0xFF8EB7F0) else Color(0xFF1E5AA8)
    val FundoPainel: Color get() = if (escuro) Color(0xFF0E141C) else Color(0xFFF1F4F8)
    val Cartao: Color get() = if (escuro) Color(0xFF182230) else Color(0xFFFFFFFF)
    val Borda: Color get() = if (escuro) Color(0xFF2E3D52) else Color(0xFFE3E8EF)
    val BordaCampo: Color get() = if (escuro) Color(0xFF3D4F68) else Color(0xFFD5DCE6)
    val CabecalhoTabela: Color get() = if (escuro) Color(0xFF223044) else Color(0xFFF6F8FB)
    val Verde: Color get() = if (escuro) Color(0xFF3DDB7A) else Color(0xFF1B7F3B)
    val Laranja: Color get() = if (escuro) Color(0xFFFFB020) else Color(0xFFE07B00)
    val Vermelho: Color get() = if (escuro) Color(0xFFFF6B6B) else Color(0xFFC62828)
    val CinzaTexto: Color get() = if (escuro) Color(0xFFA9B4C5) else Color(0xFF5B6575)
    val CinzaClaro: Color get() = if (escuro) Color(0xFF8B97A8) else Color(0xFF8A94A6)
    val Texto: Color get() = if (escuro) Color(0xFFE7EDF5) else Color(0xFF2B3546)
    val TextoForte: Color get() = if (escuro) Color(0xFFF5F8FC) else Color(0xFF3B4A60)
    val AzulClaro: Color get() = if (escuro) Color(0xFF1A3054) else Color(0xFFE7F0FB)
    val FundoAlerta: Color get() = if (escuro) Color(0xFF3A2A14) else Color(0xFFFFF4EA)
    val BordaAlerta: Color get() = if (escuro) Color(0xFF6A4A20) else Color(0xFFFBD9B5)
    val BordaCartao: BorderStroke get() = BorderStroke(1.dp, Borda)
}

private fun esquemaClaro() = lightColorScheme(
    primary = PortalCores.Marca,
    onPrimary = Color(0xFFFFFFFF),
    primaryContainer = PortalCores.AzulClaro,
    onPrimaryContainer = PortalCores.Navy,
    secondary = PortalCores.AzulIcone,
    onSecondary = Color(0xFFFFFFFF),
    secondaryContainer = PortalCores.AzulClaro,
    onSecondaryContainer = PortalCores.Navy,
    tertiary = PortalCores.CinzaTexto,
    background = PortalCores.FundoPainel,
    onBackground = PortalCores.Texto,
    surface = PortalCores.Cartao,
    onSurface = PortalCores.Texto,
    surfaceVariant = PortalCores.CabecalhoTabela,
    onSurfaceVariant = PortalCores.CinzaTexto,
    surfaceTint = PortalCores.Cartao,
    surfaceBright = PortalCores.Cartao,
    surfaceDim = PortalCores.FundoPainel,
    surfaceContainerLowest = PortalCores.Cartao,
    surfaceContainerLow = PortalCores.Cartao,
    surfaceContainer = PortalCores.Cartao,
    surfaceContainerHigh = PortalCores.CabecalhoTabela,
    surfaceContainerHighest = PortalCores.CabecalhoTabela,
    outline = PortalCores.BordaCampo,
    outlineVariant = PortalCores.Borda,
    error = PortalCores.Vermelho,
    errorContainer = Color(0xFFFDECEC),
    onErrorContainer = Color(0xFF7A1010)
)

private fun esquemaEscuro() = darkColorScheme(
    primary = PortalCores.Marca,
    onPrimary = Color(0xFFFFFFFF),
    primaryContainer = PortalCores.AzulClaro,
    onPrimaryContainer = PortalCores.Navy,
    secondary = PortalCores.AzulIcone,
    onSecondary = Color(0xFF0E141C),
    secondaryContainer = PortalCores.AzulClaro,
    onSecondaryContainer = PortalCores.Navy,
    tertiary = PortalCores.CinzaTexto,
    background = PortalCores.FundoPainel,
    onBackground = PortalCores.Texto,
    surface = PortalCores.Cartao,
    onSurface = PortalCores.Texto,
    surfaceVariant = PortalCores.CabecalhoTabela,
    onSurfaceVariant = PortalCores.CinzaTexto,
    surfaceTint = PortalCores.Cartao,
    surfaceBright = PortalCores.CabecalhoTabela,
    surfaceDim = PortalCores.FundoPainel,
    surfaceContainerLowest = Color(0xFF101820),
    surfaceContainerLow = PortalCores.Cartao,
    surfaceContainer = PortalCores.Cartao,
    surfaceContainerHigh = PortalCores.CabecalhoTabela,
    surfaceContainerHighest = PortalCores.CabecalhoTabela,
    outline = PortalCores.BordaCampo,
    outlineVariant = PortalCores.Borda,
    error = PortalCores.Vermelho,
    errorContainer = Color(0xFF3A1818),
    onErrorContainer = Color(0xFFFFC9C9)
)

/**
 * Tipografia base: Arial (sans-serif do sistema) em 14sp ≈ 14pt,
 * sem escala anisotrópica (evita fonte “esticada”).
 */
private val AppFont = FontFamily.SansSerif

private val AppTypography = Typography(
    displayLarge = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Normal, fontSize = 36.sp, lineHeight = 44.sp),
    displayMedium = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Normal, fontSize = 28.sp, lineHeight = 36.sp),
    displaySmall = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Normal, fontSize = 24.sp, lineHeight = 32.sp),
    headlineLarge = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Bold, fontSize = 22.sp, lineHeight = 28.sp),
    headlineMedium = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Bold, fontSize = 20.sp, lineHeight = 26.sp),
    headlineSmall = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Bold, fontSize = 18.sp, lineHeight = 24.sp),
    titleLarge = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Bold, fontSize = 18.sp, lineHeight = 24.sp),
    titleMedium = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.SemiBold, fontSize = 14.sp, lineHeight = 20.sp),
    titleSmall = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Medium, fontSize = 14.sp, lineHeight = 20.sp),
    bodyLarge = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Normal, fontSize = 14.sp, lineHeight = 20.sp),
    bodyMedium = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Normal, fontSize = 14.sp, lineHeight = 20.sp),
    bodySmall = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Normal, fontSize = 12.sp, lineHeight = 16.sp),
    labelLarge = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Medium, fontSize = 14.sp, lineHeight = 20.sp),
    labelMedium = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Medium, fontSize = 12.sp, lineHeight = 16.sp),
    labelSmall = TextStyle(fontFamily = AppFont, fontWeight = FontWeight.Medium, fontSize = 11.sp, lineHeight = 16.sp)
)

private val AppShapes = Shapes(
    extraSmall = RoundedCornerShape(8.dp),
    small = RoundedCornerShape(8.dp),
    medium = RoundedCornerShape(14.dp),
    large = RoundedCornerShape(16.dp),
    extraLarge = RoundedCornerShape(20.dp)
)

@Composable
fun SharedAppTheme(content: @Composable () -> Unit) {
    LaunchedEffect(Unit) { TemaPortal.sincronizar() }
    val escuro = TemaPortal.escuro
    MaterialTheme(
        colorScheme = if (escuro) esquemaEscuro() else esquemaClaro(),
        typography = AppTypography,
        shapes = AppShapes,
        content = content
    )
}
